import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { ScriptKey, StepType } from '../../../../../main/types/asset/asset.enums.ts';

import { StepGraderType } from '../../../../constants/grading.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { SAYING_SOUNDS_LESSON_TASK_TYPES, SOUNDS_WITH_ARROW_LESSON_TASK_TYPES } from '../../../../types/task.types.ts';

import { UserSayBaseStateChangeHandlerUtil } from './user-say-base.handler.ts';

/**
 * Unified handler for word pronunciation tasks
 * Handles both fast and sound-out next state
 * Used in WordReading, SayTheSounds, Rhyming, SayItFast and other word-related tasks
 */
export class UserSayWordStateChangeHandlerUtil extends UserSayBaseStateChangeHandlerUtil {
  private static _instance: UserSayWordStateChangeHandlerUtil;

  static get instance(): UserSayWordStateChangeHandlerUtil {
    if (!UserSayWordStateChangeHandlerUtil._instance) {
      UserSayWordStateChangeHandlerUtil._instance = new UserSayWordStateChangeHandlerUtil();
    }
    return UserSayWordStateChangeHandlerUtil._instance;
  }

  protected getHandlerName(): string {
    return 'UserSayWord';
  }

  protected override async onPlaying(): Promise<void> {
    console.log(`${this.getHandlerName()} onPlaying`);

    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.speakStepParts();
    await this.audioPlayerUtil.waitSpeakCompletion();

    const dispatch = await this.reduxStoreUtil.getDispatch();
    await dispatch(updateAppStateThunk({ state: AppStateEnum.Recording, blockAnimation: true })).unwrap();
  }

  protected override async determineNextState(): Promise<AppStateEnum | null> {
    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);

    // Special case: For UserSayFast, if script key is not Quick/Intro/Continue, go to Playing first
    if (
      (await this.isOneOfTasks(SAYING_SOUNDS_LESSON_TASK_TYPES)) &&
      step.stepType === StepType.UserSayFast &&
      (!step.scriptKey || ![ScriptKey.Quick, ScriptKey.Intro, ScriptKey.Continue].includes(step.scriptKey))
    ) {
      return AppStateEnum.Playing;
    }

    if (
      // All UserSayFast in sounds-with-arrow handler
      ((await this.isOneOfTasks(SOUNDS_WITH_ARROW_LESSON_TASK_TYPES)) && step.stepType === StepType.UserSayFast) ||
      // UserSoundOut in saying-sounds handler
      (step.stepType === StepType.UserSoundOut && (await this.isOneOfTasks(SAYING_SOUNDS_LESSON_TASK_TYPES)))
    ) {
      return AppStateEnum.Recording;
    }

    return null;
  }

  // Replace the old methods with a simpler GraderType implementation
  protected getGraderType(): StepGraderType {
    return StepGraderType.WORD;
  }
}

export const getUserSayWordStateChangeHandlerUtil = (): UserSayWordStateChangeHandlerUtil =>
  UserSayWordStateChangeHandlerUtil.instance;
